import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Container, Row, Col } from 'reactstrap';
import { get, isEmpty } from 'lodash-es';

// Entities
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { AppraisalModel, AppraisalEntities } from 'client/data/models/appraisal';

// Utils
import { getUrlPathFromParams } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { getAppraiserFaqContent } from 'site-modules/shared/utils/appraiser/faq-utils';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingHandler } from 'client/tracking/handler';
import { scrollToHash } from 'client/utils/scroll';
import { getQuery } from 'client/utils/location';
import { delayHeavyTask } from 'client/site-modules/shared/utils/inp-utils';
import { getIsOptimizedTraffic } from 'site-modules/shared/utils/appraisal/optimized-traffic';

// Constants
import { AppraisalMap } from 'client/tracking/maps/appraisal';
import { TrackingConstant } from 'client/tracking/constant';
import { APPRAISAL_PAGE_CREATIVE_ID } from 'site-modules/shared/constants/appraisal/appraisal';

// Shared components
import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';
import { TradeInAppraiser } from 'site-modules/shared/components/appraisal/trade-in-appraiser/trade-in-appraiser';
import { CoreBreadcrumbs } from 'site-modules/shared/components/core-breadcrumbs/core-breadcrumbs';
import { EstimatedValues } from 'site-modules/shared/components/estimated-values/estimated-values';
import { ArrowLink } from 'site-modules/shared/components/arrow-link/arrow-link';
import { Faq } from 'site-modules/shared/components/faq/faq';
import { AppraisalUtmStorage } from 'site-modules/shared/components/appraisal/appraisal-utm-storage/appraisal-utm-storage';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { ContentCollapse } from 'client/site-modules/shared/components/content-collapse/content-collapse';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';

// Components
import { MMYTitle } from 'site-modules/appraiser/components/mmy-title/mmy-title';
import { SeeAllForSaleLink } from 'client/site-modules/shared/components/appraisal/trade-in-appraiser-links/trade-in-appraiser-links';
import { DepreciationGraphic } from 'client/site-modules/appraiser/components/deprecation-graphic/deprecation-graphic';
import { AppraisalMarketingSurvey } from 'site-modules/appraiser/components/appraisal-marketing-survey/appraisal-marketing-survey';
import { OffersReviewsModule } from 'site-modules/shared/components/offers-reviews-module/offers-reviews-module';

import { getFaqContent } from './appraiser-utils';

import './appraiser.scss';

const TRADE_IN_APPRAISER_COLUMNS_SIZE = { xs: 12 };

/**
 * AppraiserContentCollapse - "isCollapsible" flag is used to determine whether the children should be wrapped
 * in with "ContentCollapse" component, otherwise it just returns it's children
 * @returns {ReactElement} Wrapped with <ContentCollapse /> or just it's children.
 */
function AppraiserContentCollapse({
  isCollapsible = false,
  textClosed = 'Other content',
  textOpen = 'Hide other content',
  children,
}) {
  if (isCollapsible) {
    return (
      <ContentCollapse
        textClosed={textClosed}
        textOpen={textOpen}
        btnTrackingId="appraisal_below_the_fold"
        btnIgnoreTrackingValue={false}
        justify={null}
        btnContainerClasses="m-0 p-0 pb-1"
        btnClasses="appraiser-content-collapse-btn p-0 size-16 text-primary-darker"
        btnTextClasses="fw-normal"
        isEclipseFade={false}
      >
        {children}
      </ContentCollapse>
    );
  }

  return children;
}

AppraiserContentCollapse.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  isCollapsible: PropTypes.bool,
  textClosed: PropTypes.string,
  textOpen: PropTypes.string,
};

const CoreBreadcrumbsSection = ({ estimatedVehicle, params }) => (
  <Col xs={12}>
    <ErrorBoundary>
      <CoreBreadcrumbs
        className="px-0 mb-0 text-white text-shadow-contrast"
        useDefaultYearV2
        vehicle={{
          make: { name: estimatedVehicle.makeName, slug: estimatedVehicle.makeSlug },
          model: { name: estimatedVehicle.modelName, slug: estimatedVehicle.modelSlug },
          submodels: estimatedVehicle.submodels,
          year: estimatedVehicle.year,
        }}
        breadcrumbsParams={{
          params,
          pageDisplayName: 'Appraisal value',
          includeLastLink: true,
          urlPath: getUrlPathFromParams(params, 'appraisal-value'),
        }}
      />
    </ErrorBoundary>
  </Col>
);

CoreBreadcrumbsSection.propTypes = {
  estimatedVehicle: PropTypes.shape({}).isRequired,
  params: PropTypes.shape({}).isRequired,
};

export class AppraiserUI extends Component {
  static propTypes = {
    estimatedVehicle: VehicleEntities.StyleWithoutSubmodels,
    appraisalStyles: AppraisalEntities.Styles,
    params: PropTypes.shape({
      make: PropTypes.string.isRequired,
      model: PropTypes.string.isRequired,
      year: PropTypes.string.isRequired,
      styleId: PropTypes.string,
    }).isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
    isMobile: PropTypes.bool,
    isLandingReferer: PropTypes.bool,
    isAppExtensionPage: PropTypes.bool,
    estimatedAppraisalVin: PropTypes.string,
    modsRecord: PropTypes.shape({}),
    setModelValue: PropTypes.func.isRequired,
    LocationAndStyleStepComponent: PropTypes.elementType.isRequired,
  };

  static defaultProps = {
    estimatedVehicle: null,
    appraisalStyles: [],
    location: {},
    isMobile: false,
    isLandingReferer: false,
    isAppExtensionPage: false,
    estimatedAppraisalVin: null,
    modsRecord: null,
  };

  constructor(props) {
    super(props);
    AppraisalUtmStorage.checkAndSetSemCampaignAppraisals(this.props.location);
    AppraisalUtmStorage.checkAndSetSemCampaignFacebook(this.props.location);
    this.state = {
      showEstimatedValuesWidget: true,
      vehicleOverride: null,
      showPartnerFaqQuestions: false,
      showLinkWidget: true,
      creativeIdOverride: null,
    };
  }

  componentDidMount() {
    const { location } = this.props;
    const { modsId } = getQuery(location);

    TrackingHandler.useMap(AppraisalMap);

    const hash = get(location, ['hash'], '');
    scrollToHash(hash, true);

    if (modsId) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_END,
        event_data: {
          action_name: TrackingConstant.ACTION_APPRAISE_VEHICLE,
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
          subaction_name: TrackingConstant.MODS_PREPOPULATED,
          creative_id: APPRAISAL_PAGE_CREATIVE_ID,
          value: 'modsid_in_url',
        },
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { modsRecord, location } = this.props;
    const { modsId } = getQuery(location);

    if (modsId && !isEmpty(modsRecord) && isEmpty(prevProps.modsRecord)) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_RECEIVE_EPO,
          subaction_name: TrackingConstant.MODS_RECORD_RETURNED,
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
          creative_id: 'edm-entry-mods',
          value: modsId,
        },
      });
    }
  }

  onVehicleOverrideContinue = (vehicleOverride, creativeIdOverride) => {
    delayHeavyTask(() => {
      this.setState({
        vehicleOverride,
        creativeIdOverride,
        // Hide widgets now to reduce re-renders on step change
        showEstimatedValuesWidget: false,
        showLinkWidget: false,
      });
    });
  };

  setAppraiseFinished = isFinished => {
    this.props.setModelValue('isAppraiseFinished', AppraisalModel, isFinished);
  };

  hideEstimatedValuesWidget = () => {
    this.setState({
      showEstimatedValuesWidget: false,
    });
  };

  showPartnerFaqQuestions = isVisible => {
    this.setState({
      showPartnerFaqQuestions: isVisible,
    });
  };

  hideLinkWidget = () => {
    this.setState({
      showLinkWidget: false,
    });
  };

  renderTradeInAppraiser = (extraProps = {}) => {
    const { vehicleOverride, creativeIdOverride } = this.state;
    const {
      estimatedVehicle,
      params,
      isMobile,
      isAppExtensionPage,
      estimatedAppraisalVin,
      LocationAndStyleStepComponent,
    } = this.props;

    return (
      <TradeInAppraiser
        columnsSize={TRADE_IN_APPRAISER_COLUMNS_SIZE}
        estimatedVehicle={estimatedVehicle}
        isMobile={isMobile}
        isExtendedReport
        onEstimatedValuesWidgetHide={this.hideEstimatedValuesWidget}
        onPartnerFaqQuestionsShow={this.showPartnerFaqQuestions}
        vehicleOverride={vehicleOverride}
        onLinkWidgetHide={this.hideLinkWidget}
        setAppraiseFinished={this.setAppraiseFinished}
        creativeIdOverride={creativeIdOverride}
        estimatedAppraisalVin={estimatedAppraisalVin}
        isAppExtensionPage={isAppExtensionPage}
        LocationAndStyleStepComponent={LocationAndStyleStepComponent}
        params={params}
        {...extraProps}
      />
    );
  };

  render() {
    const { showLinkWidget, showEstimatedValuesWidget, showPartnerFaqQuestions } = this.state;
    const { estimatedVehicle, params, location, isMobile, appraisalStyles, isLandingReferer, isAppExtensionPage } =
      this.props;

    if (!estimatedVehicle) return null;

    const { id: estimatedStyleId, makeName, modelName, year } = estimatedVehicle;
    const commonFaqEntries = getFaqContent({
      appraisalStyles,
      estimatedStyleId,
      estimatedVehicle,
      pathname: location.pathname,
    });
    const faqEntries = (showPartnerFaqQuestions && getAppraiserFaqContent(commonFaqEntries)) || commonFaqEntries;
    const convertedYearParam = Number(params.year);

    const isOptimizedFunnelTraffic = getIsOptimizedTraffic({ isAppExtensionPage, location });

    return (
      <Fragment>
        <main id="main-content" tabIndex={-1} className="appraiser" data-tracking-parent="edm-entry-appraisal-page">
          <div className="main-content-container overflow-hidden mb-1">
            <Container>
              <Row>
                {!isAppExtensionPage && <CoreBreadcrumbsSection estimatedVehicle={estimatedVehicle} params={params} />}
                <Col xs={12} lg={9}>
                  <MMYTitle
                    vehicle={estimatedVehicle}
                    year={convertedYearParam}
                    isAppExtensionPage={isAppExtensionPage}
                    appraisalStyles={appraisalStyles}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12} lg={10}>
                  <ErrorBoundary>{this.renderTradeInAppraiser()}</ErrorBoundary>
                </Col>
              </Row>
            </Container>
          </div>
          <div className="content-above bg-white">
            <Container>
              <Row>
                <Col xs={12} lg={10}>
                  {!isAppExtensionPage ? (
                    <AppraiserContentCollapse isCollapsible={isLandingReferer || isOptimizedFunnelTraffic}>
                      {showEstimatedValuesWidget && (
                        // This "section" wrapper is VERY important for client-side rendering of optimized traffic
                        <section>
                          <ErrorBoundary>
                            <EstimatedValues
                              vehicle={estimatedVehicle}
                              isMobile={isMobile}
                              onContinue={this.onVehicleOverrideContinue}
                            />
                          </ErrorBoundary>
                          <ErrorBoundary>
                            <DepreciationGraphic
                              onContinue={this.onVehicleOverrideContinue}
                              vehicle={estimatedVehicle}
                              appraisalStyles={appraisalStyles}
                              isMobile={isMobile}
                              pathname={location.pathname}
                            />
                          </ErrorBoundary>
                          <section className="reviews-module-block bg-white py-2 px-1 px-md-2 mt-1_5">
                            <MinHeightWrapper componentType="REVIEWS_MODULE_APPRAISAL_STYLE">
                              <OffersReviewsModule isWithSpinner />
                            </MinHeightWrapper>
                          </section>
                          <ErrorBoundary>
                            <div className="my-1_5 pos-r">
                              <ArrowLink to="/sell-car/" data-tracking-id="click_sell_my_car" className="mb-1">
                                Sell my {year} {makeName} {modelName} with Edmunds
                              </ArrowLink>
                              <SeeAllForSaleLink
                                vehicle={estimatedVehicle}
                                title={`Shop for a used ${makeName} ${modelName} near you`}
                              />
                            </div>
                          </ErrorBoundary>
                        </section>
                      )}
                      <AppraisalMarketingSurvey />
                      <Faq entries={faqEntries} isOpenCollapseOnLoad={false} />
                    </AppraiserContentCollapse>
                  ) : (
                    <AppraisalMarketingSurvey />
                  )}
                </Col>
              </Row>

              {showLinkWidget && !isAppExtensionPage && (
                <AppraiserContentCollapse
                  isCollapsible={isLandingReferer || isOptimizedFunnelTraffic}
                  textClosed="Show sponsored cars and related links"
                  textOpen="Hide sponsored cars and related links"
                >
                  <PageLinksWidget divideSections={false} title="Related information" hasTopDivider />
                </AppraiserContentCollapse>
              )}
            </Container>
          </div>
        </main>
      </Fragment>
    );
  }
}
